/**
 * FileRepository - Repositório de Arquivos
 * Persiste metadados de arquivos no IndexedDB
 * Os arquivos em si são armazenados como Blob
 */

import { openDatabase } from './IndexedDBSetup';

const STORE_NAME = 'files';

/**
 * Gera ID local único
 */
const generateLocalId = () => {
  const now = new Date();
  const dateStr = now.toISOString().replace(/[-:T]/g, '').slice(0, 14);
  const random = Math.random().toString(36).substring(2, 5).toUpperCase();
  return `LOC-FILE-${dateStr}-${random}`;
};

/**
 * Cria metadados de arquivo
 */
export const create = async (surveyId, localSurveyId, fileData) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const localId = generateLocalId();
    const now = new Date().toISOString();

    const file = {
      ...fileData,
      id: localId,
      localId,
      surveyId,
      localSurveyId,
      syncStatus: 'pending',
      createdAt: now,
      updatedAt: now,
      serverId: null,
    };

    const request = store.add(file);

    request.onsuccess = () => {
      resolve(file);
    };

    request.onerror = () => {
      reject(new Error(`Erro ao criar arquivo: ${request.error}`));
    };
  });
};

/**
 * Atualiza metadados de arquivo
 */
export const update = async (id, data) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;

      if (!existing) {
        reject(new Error(`Arquivo com ID ${id} não encontrado`));
        return;
      }

      const updated = {
        ...existing,
        ...data,
        id: existing.id,
        localId: existing.localId,
        syncStatus: 'pending',
        updatedAt: new Date().toISOString(),
      };

      const putRequest = store.put(updated);

      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(new Error(`Erro ao atualizar arquivo: ${putRequest.error}`));
    };

    getRequest.onerror = () => reject(new Error(`Erro ao buscar arquivo: ${getRequest.error}`));
  });
};

/**
 * Remove arquivo
 */
export const remove = async (id) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error(`Erro ao remover arquivo: ${request.error}`));
  });
};

/**
 * Busca arquivo por ID
 */
export const getById = async (id) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(new Error(`Erro ao buscar arquivo: ${request.error}`));
  });
};

/**
 * Busca arquivo por localId
 */
export const getByLocalId = async (localId) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('localId');
    const request = index.get(localId);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(new Error(`Erro ao buscar arquivo: ${request.error}`));
  });
};

/**
 * Lista arquivos de um levantamento (por localSurveyId)
 */
export const getBySurveyLocalId = async (localSurveyId) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('localSurveyId');
    const request = index.getAll(localSurveyId);

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(new Error(`Erro ao listar arquivos: ${request.error}`));
  });
};

/**
 * Lista arquivos por tipo
 */
export const getBySurveyLocalIdAndType = async (localSurveyId, fileType) => {
  const files = await getBySurveyLocalId(localSurveyId);
  return files.filter(f => f.file_type === fileType);
};

/**
 * Lista arquivos pendentes de sync
 */
export const getPending = async () => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('syncStatus');
    const request = index.getAll('pending');

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(new Error(`Erro ao buscar pendientes: ${request.error}`));
  });
};

/**
 * Atualiza status de sync
 */
export const updateSyncStatus = async (id, status, serverId = null) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;
      if (!existing) {
        reject(new Error(`Arquivo com ID ${id} não encontrado`));
        return;
      }

      const updated = {
        ...existing,
        syncStatus: status,
        serverId: serverId !== null ? serverId : existing.serverId,
        syncedAt: status === 'synced' ? new Date().toISOString() : existing.syncedAt,
      };

      const putRequest = store.put(updated);
      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(new Error(`Erro ao atualizar sync status: ${putRequest.error}`));
    };

    getRequest.onerror = () => reject(new Error(`Erro ao buscar arquivo: ${getRequest.error}`));
  });
};

/**
 * Atualiza serverId após sincronização
 */
export const updateServerId = async (localId, serverId) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('localId');
    const getRequest = index.get(localId);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;
      if (!existing) {
        reject(new Error(`Arquivo com localId ${localId} não encontrado`));
        return;
      }

      const updated = { ...existing, serverId, syncStatus: 'synced' };
      const putRequest = store.put(updated);
      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(new Error(`Erro ao atualizar serverId: ${putRequest.error}`));
    };

    getRequest.onerror = () => reject(new Error(`Erro ao buscar arquivo: ${getRequest.error}`));
  });
};

/**
 * Remove todos os arquivos de um levantamento
 */
export const removeBySurveyLocalId = async (localSurveyId) => {
  const db = await openDatabase();
  const files = await getBySurveyLocalId(localSurveyId);

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    let completed = 0;

    if (files.length === 0) {
      resolve();
      return;
    }

    files.forEach(file => {
      const request = transaction.objectStore(STORE_NAME).delete(file.id);
      request.onsuccess = () => {
        completed++;
        if (completed === files.length) resolve();
      };
      request.onerror = () => reject(new Error(`Erro ao remover arquivo ${file.id}`));
    });
  });
};

/**
 * Salva arquivo físico (Blob) no IndexedDB
 */
export const saveBlob = async (localId, blob) => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('localId');
    const getRequest = index.get(localId);

    getRequest.onsuccess = () => {
      const existing = getRequest.result;
      if (!existing) {
        reject(new Error(`Arquivo com localId ${localId} não encontrado`));
        return;
      }

      const updated = { ...existing, blob };
      const putRequest = store.put(updated);
      putRequest.onsuccess = () => resolve(updated);
      putRequest.onerror = () => reject(new Error(`Erro ao salvar blob: ${putRequest.error}`));
    };

    getRequest.onerror = () => reject(new Error(`Erro ao buscar arquivo: ${getRequest.error}`));
  });
};

/**
 * Recupera blob de arquivo
 */
export const getBlob = async (localId) => {
  const file = await getByLocalId(localId);
  return file?.blob || null;
};

/**
 * Conta arquivos pendentes
 */
export const countPending = async () => {
  const pending = await getPending();
  return pending.length;
};

export default {
  create,
  update,
  remove,
  getById,
  getByLocalId,
  getBySurveyLocalId,
  getBySurveyLocalIdAndType,
  getPending,
  updateSyncStatus,
  updateServerId,
  removeBySurveyLocalId,
  saveBlob,
  getBlob,
  countPending,
};
